<template>
  <div class="navbar">
    <div class="left-section">
      <el-breadcrumb separator="/" class="navbar-breadcrumb">
        <el-breadcrumb-item
          v-for="item in breadcrumbs"
          :key="item.path"
          :to="item.path"
        >
          {{ item.meta && item.meta.title ? item.meta.title : '' }}
        </el-breadcrumb-item>
      </el-breadcrumb>
    </div>

    <div class="right-section">
      <span class="navbar-date">{{ currentDate }}</span>

      <span class="navbar-divider" aria-hidden="true"></span>

      <el-dropdown trigger="click" @command="handleCommand" class="user-dropdown-wrap">
        <span class="user-dropdown">
          <span class="user-avatar">{{ avatarInitial }}</span>
          <span class="user-name">{{ username || 'admin' }}</span>
          <i class="el-icon-arrow-down user-caret"></i>
        </span>
        <el-dropdown-menu slot="dropdown" class="navbar-dropdown-menu">
          <el-dropdown-item disabled>
            <i class="el-icon-user"></i>
            个人中心
          </el-dropdown-item>
          <el-dropdown-item divided command="logout">
            <i class="el-icon-switch-button"></i>
            退出登录
          </el-dropdown-item>
        </el-dropdown-menu>
      </el-dropdown>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  name: 'Navbar',
  data() {
    return {
      currentDate: ''
    }
  },
  computed: {
    ...mapGetters(['username']),
    breadcrumbs() {
      const matched = this.$route.matched.filter(
        item => item.meta && item.meta.title
      )
      return matched
    },
    avatarInitial() {
      const name = this.username || 'admin'
      return name.charAt(0).toUpperCase()
    }
  },
  created() {
    this.refreshDate()
    // Re-evaluate at midnight so the displayed date stays correct on long sessions.
    const msUntilMidnight =
      new Date().setHours(24, 0, 0, 0) - Date.now()
    this._dateTimer = setTimeout(() => {
      this.refreshDate()
      this._dateInterval = setInterval(this.refreshDate, 24 * 60 * 60 * 1000)
    }, msUntilMidnight)
  },
  beforeDestroy() {
    if (this._dateTimer) clearTimeout(this._dateTimer)
    if (this._dateInterval) clearInterval(this._dateInterval)
  },
  methods: {
    refreshDate() {
      const weekdays = ['周日', '周一', '周二', '周三', '周四', '周五', '周六']
      const now = new Date()
      const y = now.getFullYear()
      const m = now.getMonth() + 1
      const d = now.getDate()
      const w = weekdays[now.getDay()]
      this.currentDate = `${y}年${m}月${d}日 ${w}`
    },
    async handleCommand(command) {
      if (command === 'logout') {
        await this.$store.dispatch('user/logout')
        this.$router.push(`/login?redirect=${this.$route.fullPath}`)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";

.navbar {
  height: $navbar-height;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  background: $navbar-bg;
  box-shadow: $navbar-shadow;
  border-bottom: 1px solid rgba(0, 0, 0, 0.02);
  font-family: $font-family;
}

.left-section,
.right-section {
  display: flex;
  align-items: center;
}

.right-section {
  gap: 16px;
}

// -----------------------------------------------------------------------------
// Breadcrumb — inherit base look from Element UI but recolor for our palette.
// -----------------------------------------------------------------------------
.navbar-breadcrumb {
  font-size: 14px;
  line-height: 1;

  ::v-deep .el-breadcrumb__item {
    .el-breadcrumb__inner {
      color: $text-secondary;
      font-weight: 400;
      transition: $transition-base;

      &.is-link:hover {
        color: $primary-color;
      }
    }

    &:last-child .el-breadcrumb__inner {
      color: $text-primary;
      font-weight: 600;
    }
  }
}

// -----------------------------------------------------------------------------
// Date pill
// -----------------------------------------------------------------------------
.navbar-date {
  font-size: 13px;
  color: $text-secondary;
  letter-spacing: 0.3px;
  white-space: nowrap;
}

.navbar-divider {
  width: 1px;
  height: 18px;
  background-color: $border-color;
}

// -----------------------------------------------------------------------------
// User area
// -----------------------------------------------------------------------------
.user-dropdown-wrap {
  outline: none;
}

.user-dropdown {
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px 4px 4px;
  border-radius: 999px;
  transition: $transition-base;
  user-select: none;

  &:hover {
    background-color: rgba($primary-color, 0.06);

    .user-caret {
      color: $primary-color;
    }
  }
}

.user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: $primary-color;
  color: #ffffff;
  font-size: 15px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  letter-spacing: 0;
  box-shadow: 0 2px 6px rgba(74, 123, 247, 0.25);
  transition: $transition-base;
}

.user-name {
  font-size: 14px;
  font-weight: 500;
  color: $text-primary;
  line-height: 1;
}

.user-caret {
  font-size: 12px;
  color: $text-secondary;
  transition: $transition-base;
}
</style>

<!--
  The dropdown panel is teleported to <body>, so its styles are intentionally
  unscoped. They are namespaced via `.navbar-dropdown-menu` to stay safe.
-->
<style lang="scss">
@import "@/assets/styles/variables.scss";

.el-dropdown-menu.navbar-dropdown-menu {
  padding: 6px;
  border-radius: $border-radius;
  box-shadow: 0 6px 24px rgba(0, 21, 41, 0.12);
  border: 1px solid $border-color;
  min-width: 160px;

  .el-dropdown-menu__item {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 36px;
    line-height: 36px;
    padding: 0 12px;
    font-size: 14px;
    color: $text-regular;
    border-radius: $border-radius-sm;
    transition: $transition-base;

    i {
      font-size: 14px;
      color: $text-secondary;
    }

    &:not(.is-disabled):hover,
    &:not(.is-disabled):focus {
      background-color: rgba($primary-color, 0.08);
      color: $primary-color;

      i {
        color: $primary-color;
      }
    }
  }

  .el-dropdown-menu__item--divided {
    margin-top: 6px;
    border-top: 1px solid $border-color;

    &:before {
      display: none;
    }
  }
}
</style>
